// @spec AI-STR-028
'use client';

import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  Bot,
  ChevronDown,
  ChevronRight,
  Flag,
  Loader2,
  RefreshCw,
  User,
} from 'lucide-react';
import * as React from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import type {
  AdminConversationDetail,
  AdminSourceReference,
} from '@/features/settings/ai/api/use-conversation-review';
import {
  useAdminConversation,
  useToggleConversationFlag,
} from '@/features/settings/ai/api/use-conversation-review';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/ai-conversations/$conversationId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.aiConversationDetails'),
  }),
  component: AIConversationDetailPage,
});

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  archived: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SourcesList({ sources }: { sources: AdminSourceReference[] }) {
  const { t } = useTranslation('settings');
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="mt-2">
      <button
        type="button"
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {t('aiConversationDetailPage.sources', { count: sources.length })}
      </button>
      {expanded && (
        <ul className="mt-1 space-y-1 pl-4">
          {sources.map((source, i) => (
            <li key={i} className="text-xs text-muted-foreground">
              <span className="font-medium">{source.title}</span>
              {source.confidence != null && (
                <span className="ml-1 opacity-60">({Math.round(source.confidence * 100)}%)</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConversationMessages({ conversation }: { conversation: AdminConversationDetail }) {
  const { t } = useTranslation('settings');
  return (
    <div className="space-y-4">
      {conversation.messages.map((msg) => {
        const isUser = msg.role === 'user';
        return (
          <div key={msg.id} className="flex gap-3">
            <div
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                isUser ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              )}
            >
              {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  {isUser
                    ? (conversation.userName ?? t('aiConversationDetailPage.userFallback'))
                    : t('aiConversationDetailPage.aiAssistant')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(msg.createdAt)}
                </span>
                {msg.model && (
                  <Badge variant="outline" className="text-xs">
                    {msg.model}
                  </Badge>
                )}
                {msg.tokensUsed != null && (
                  <span className="text-xs text-muted-foreground">
                    {t('aiConversationDetailPage.tokensUsed', { count: msg.tokensUsed })}
                  </span>
                )}
              </div>
              <div className="mt-1 whitespace-pre-wrap text-sm">{msg.content}</div>
              {msg.sources && msg.sources.length > 0 && <SourcesList sources={msg.sources} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AIConversationDetailPage() {
  const { t } = useTranslation('settings');
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();
  const { conversationId } = useParams({
    from: '/_authenticated/settings/ai-conversations/$conversationId',
  });
  const {
    data: conversation,
    isLoading,
    isError,
    error,
    refetch,
  } = useAdminConversation(conversationId);
  const toggleFlag = useToggleConversationFlag();

  const statusLabel = React.useMemo(
    () =>
      t(`aiConversationDetailPage.status.${conversation?.status ?? ''}`, {
        defaultValue: conversation?.status ?? '',
      }),
    [conversation?.status, t]
  );

  const handleFlagToggle = React.useCallback(() => {
    if (!conversation) return;
    toggleFlag.mutate({
      conversationId: conversation.id,
      flagged: !conversation.flagged,
    });
  }, [conversation, toggleFlag]);

  if (isRoleLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          <h1 className="text-3xl font-semibold text-foreground">
            {t('aiConversationDetailPage.accessDeniedTitle')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('aiConversationDetailPage.accessDeniedDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <Button variant="outline" asChild>
          <Link to="/settings/ai-conversations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('aiConversationDetailPage.backToList')}
          </Link>
        </Button>

        {isLoading ? (
          <div className="flex h-[320px] items-center justify-center rounded-lg border">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('aiConversationDetailPage.errorTitle')}</AlertTitle>
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : t('aiConversationDetailPage.errorDescription')}
            </AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => void refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('buttons.tryAgain')}
            </Button>
          </Alert>
        ) : !conversation ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('aiConversationDetailPage.notFoundTitle')}</AlertTitle>
            <AlertDescription>{t('aiConversationDetailPage.notFoundDescription')}</AlertDescription>
          </Alert>
        ) : (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2">
                    <CardTitle className="break-words">{conversation.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {conversation.userName ?? t('aiConversationDetailPage.unknownUser')} &middot;{' '}
                      {conversation.userEmail ?? ''}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Badge
                        variant="secondary"
                        className={cn('text-xs capitalize', STATUS_COLORS[conversation.status])}
                      >
                        {statusLabel}
                      </Badge>
                      {conversation.moduleContext?.module && (
                        <Badge variant="outline" className="text-xs">
                          {conversation.moduleContext.module.toUpperCase()}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {t('aiConversationDetailPage.messagesCount', {
                          count: conversation.messageCount,
                        })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t('aiConversationDetailPage.tokensUsed', {
                          count: conversation.tokenCount,
                        })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t('aiConversationDetailPage.createdAt', {
                          date: formatDateTime(conversation.createdAt),
                        })}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleFlagToggle}
                    disabled={toggleFlag.isPending}
                    title={
                      conversation.flagged
                        ? t('aiConversationDetailPage.unflagConversation')
                        : t('aiConversationDetailPage.flagConversation')
                    }
                    className={cn(
                      conversation.flagged
                        ? 'text-red-500 hover:text-red-600'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Flag className={cn('h-4 w-4', conversation.flagged && 'fill-current')} />
                  </Button>
                </div>
              </CardHeader>
              {conversation.summary && (
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground italic">{conversation.summary}</p>
                </CardContent>
              )}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('aiConversationDetailPage.messagesSectionTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[60vh] pr-4">
                  <ConversationMessages conversation={conversation} />
                </ScrollArea>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
